import React from 'react';
import { ArrowDownLeft, Calendar, Filter, MoreVertical } from 'lucide-react';
import { cn } from '../lib/utils';

interface DepreciationScheduleReportProps {
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  isFilterMenuOpen: boolean;
  setIsFilterMenuOpen: (open: boolean) => void;
  isMoreMenuOpen: boolean;
  setIsMoreMenuOpen: (open: boolean) => void;
  showDecimals: boolean;
  setShowDecimals: (show: boolean) => void;
  setActiveReport: (report: string | null) => void;
}

export const DepreciationScheduleReport: React.FC<DepreciationScheduleReportProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  isFilterMenuOpen,
  setIsFilterMenuOpen,
  isMoreMenuOpen,
  setIsMoreMenuOpen,
  showDecimals,
  setShowDecimals,
  setActiveReport
}) => {
  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date range:</span>
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px) font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Columns</span>
            <select 
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="7">7 columns selected</option>
            </select>
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Grouping/Summarizing</span>
            <select 
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="cost-account">Group by Cost Account</option>
            </select>
          </div>

          <button 
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
          >
            <Filter className="w-4 h-4" />
            Filter
          </button>

          <div className="relative">
            <button 
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
            >
              <MoreVertical className="w-4 h-4" />
              More
            </button>
            
            {isMoreMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl shadow-xl z-50 p-4 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Show</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showDecimals} onChange={(e) => setShowDecimals(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Decimals</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <button className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all">
            Update
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Depreciation Schedule</h1>
            <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
              Demo Company (Global)
            </p>
            <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
              For the month ended {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <button className="px-4 py-2 bg-white border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414] hover:text-white transition-all">
            Reorder columns
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Name</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Cost</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Opening Value</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Purchases</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Disposals</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Depreciation</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Closing Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              <tr className="bg-[#141414]/5">
                <td colSpan={7} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Computer Equipment</td>
              </tr>
              <tr className="hover:bg-[#141414]/5 transition-colors">
                <td className="p-4 text-xs font-bold">LCD Display</td>
                <td className="p-4 text-right font-mono text-xs">6,500.00</td>
                <td className="p-4 text-right font-mono text-xs">6,500.00</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">6,500.00</td>
              </tr>
              <tr className="font-bold bg-[#141414]/2">
                <td className="p-4 text-xs italic serif">Total Computer Equipment</td>
                <td className="p-4 text-right font-mono text-xs">6,500.00</td>
                <td className="p-4 text-right font-mono text-xs">6,500.00</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">6,500.00</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
